/**
 * spiritualComplexityResolver.js
 *
 * Scores the spiritual complexity of the current pastoral situation.
 * Composite of emotion intensity, risk level, ethics signals,
 * tension presence, and conversation depth.
 *
 * Levels:
 *   simple        — straightforward; standard pastoral response appropriate
 *   layered       — multiple factors; more care needed but manageable
 *   delicate      — fragile situation; requires nuance and restraint
 *   deeply_complex — high stakes, unresolved tension, profound ambiguity
 */

/**
 * @param {{
 *   emotion:            object|null,
 *   ethics:             object,        — from assessment.ethics
 *   intent:             string,
 *   riskLevel:          string,
 *   conversationLength: number,
 *   hasTension:         boolean,
 * }} params
 * @returns {{
 *   complexity:  "simple"|"layered"|"delicate"|"deeply_complex",
 *   score:       number,
 *   reasons:     string[],
 * }}
 */
export function resolveSpiritualComplexity({
  emotion,
  ethics,
  intent,
  riskLevel         = "none",
  conversationLength = 0,
  hasTension        = false,
}) {
  let score   = 0;
  const reasons = [];

  // ── Emotion intensity ──────────────────────────────────────────────
  const dom = emotion?.dominantEmotion ?? "none";
  const highIntensityEmotions = ["grief", "shame", "spiritual_confusion", "crisis"];
  const midIntensityEmotions  = ["conflict", "anxiety", "doubt", "loneliness"];

  if (highIntensityEmotions.includes(dom)) {
    score += 3;
    reasons.push(`emoción intensa: ${dom}`);
  } else if (midIntensityEmotions.includes(dom)) {
    score += 1;
    reasons.push(`emoción presente: ${dom}`);
  }

  // ── Risk level ────────────────────────────────────────────────────
  if (riskLevel === "critical") { score += 4; reasons.push("riesgo crítico"); }
  else if (riskLevel === "high")   { score += 3; reasons.push("riesgo alto"); }
  else if (riskLevel === "medium") { score += 1; reasons.push("riesgo moderado"); }

  // ── Ethics detected categories ────────────────────────────────────
  const detected = ethics?.detected ?? [];
  const heavyEthics = ["apostasy", "marital_crisis", "discipline", "abuse", "crisis"];
  const detected_heavy = detected.filter((d) => heavyEthics.includes(d));
  if (detected_heavy.length >= 2) { score += 3; reasons.push("múltiples señales éticas graves"); }
  else if (detected_heavy.length === 1) { score += 2; reasons.push(`señal ética: ${detected_heavy[0]}`); }
  else if (detected.length > 0)         { score += 1; reasons.push("señales éticas presentes"); }

  // ── Pastoral tension presence ─────────────────────────────────────
  if (hasTension) { score += 2; reasons.push("tensión pastoral activa"); }

  // ── Deep conversation ─────────────────────────────────────────────
  if (conversationLength >= 10) { score += 1; reasons.push("conversación extensa"); }

  // ── Intent modifiers ──────────────────────────────────────────────
  if (intent === "reflection" || intent === "prayer") {
    score += 1;
    reasons.push("contexto reflexivo/espiritual");
  }

  // ── Map score to complexity level ─────────────────────────────────
  let complexity;
  if (score >= 8)      complexity = "deeply_complex";
  else if (score >= 5) complexity = "delicate";
  else if (score >= 2) complexity = "layered";
  else                 complexity = "simple";

  return { complexity, score, reasons };
}
